// Small UI pieces the admin screens (S8 to S12) use, on top of M10's shadcn/ui copies in
// src/components/ui (Button, Input, Textarea, NativeSelect, Badge, Dialog) and its shared states.
// What M10 doesn't have (a labelled field, a switch, panels, notices, copy buttons, a confirm
// dialog that shows the server's refusal) lives here, in M11's area.
import { Check, Copy, Info, Loader2, TriangleAlert } from 'lucide-react';
import { Switch as RadixSwitch } from 'radix-ui';
import { type ComponentProps, type ReactNode, useId, useState } from 'react';
import { EmptyState as SharedEmptyState } from '@/components/EmptyState';
import { LoadingState } from '@/components/LoadingState';
import { Badge as SharedBadge } from '@/components/ui/badge';
import { Button as SharedButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input as SharedInput } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Textarea as SharedTextarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

// ---- Buttons ------------------------------------------------------------------------------------

const BUTTON_VARIANTS = {
  primary: 'default',
  secondary: 'secondary',
  ghost: 'ghost',
  danger: 'destructive',
} as const;
const BUTTON_SIZES = { sm: 'sm', md: 'default' } as const;

export interface ButtonProps extends ComponentProps<'button'> {
  variant?: keyof typeof BUTTON_VARIANTS;
  size?: keyof typeof BUTTON_SIZES;
  busy?: boolean;
}

export function Button({ variant = 'secondary', size = 'md', busy = false, children, disabled, ...rest }: ButtonProps) {
  return (
    <SharedButton
      variant={BUTTON_VARIANTS[variant]}
      size={BUTTON_SIZES[size]}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <Loader2 aria-hidden="true" className="animate-spin" /> : null}
      {children}
    </SharedButton>
  );
}

// ---- Form controls ------------------------------------------------------------------------------

export const Input = SharedInput;
export const Textarea = SharedTextarea;

export function Select({ className, ...rest }: ComponentProps<'select'>) {
  return <NativeSelect className={className} {...rest} />;
}

/** A labelled field: label, control, hint and the server's or the form's error. */
export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  /** Receives the ids to put on the control. */
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <label htmlFor={id} className="text-sm font-medium">
        {label}
        {required ? (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        ) : null}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint ? (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  disabled,
  id,
  testId,
}: {
  checked: boolean;
  onCheckedChange: (value: boolean) => void;
  /** Accessible name. */
  label: string;
  disabled?: boolean;
  id?: string;
  /** `data-testid` on the switch itself, so a test can read its checked state. */
  testId?: string;
}) {
  return (
    <RadixSwitch.Root
      id={id}
      data-testid={testId}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={label}
      className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-border-strong bg-surface-raised transition-colors disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary"
    >
      <RadixSwitch.Thumb className="block size-4.5 translate-x-0.5 rounded-full bg-text shadow transition-transform data-[state=checked]:translate-x-5.5 data-[state=checked]:bg-on-primary" />
    </RadixSwitch.Root>
  );
}

// ---- Layout pieces ------------------------------------------------------------------------------

export function Panel({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('rounded-lg border border-border bg-surface p-4 shadow-sm sm:p-5', className)}>
      {title || actions ? (
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            {title ? <h2 className="text-base font-semibold">{title}</h2> : null}
            {description ? <p className="mt-0.5 text-sm text-muted">{description}</p> : null}
          </div>
          {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function PageTitle({
  crumbs,
  title,
  badge,
  actions,
}: {
  crumbs?: ReactNode;
  title: ReactNode;
  badge?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {crumbs ? <div className="mb-1 text-sm text-muted">{crumbs}</div> : null}
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-2xl font-semibold text-primary">{title}</h1>
          {badge}
        </div>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

const BADGE_TONES = {
  neutral: 'outline',
  info: 'info',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
} as const;

export function Badge({
  tone = 'neutral',
  children,
  className,
  title,
  wrap = false,
}: {
  tone?: keyof typeof BADGE_TONES;
  children: ReactNode;
  className?: string;
  title?: string;
  /** Long text (reasons, details) wraps instead of running off a phone screen. */
  wrap?: boolean;
}) {
  return (
    <SharedBadge
      variant={BADGE_TONES[tone]}
      className={cn('font-semibold', wrap && 'max-w-full whitespace-normal break-words', className)}
      title={title}
    >
      {children}
    </SharedBadge>
  );
}

const NOTICE_TONES = {
  info: 'border-primary/40 bg-primary/5',
  success: 'border-success/50 bg-success/10',
  warning: 'border-warning/60 bg-warning/10',
  danger: 'border-danger/50 bg-danger/10',
} as const;

export function Notice({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: keyof typeof NOTICE_TONES;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  const Icon = tone === 'warning' || tone === 'danger' ? TriangleAlert : tone === 'success' ? Check : Info;
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-2 rounded-md border p-3 text-sm', NOTICE_TONES[tone], className)}
    >
      <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <div className={title ? 'mt-0.5' : undefined}>{children}</div> : null}
      </div>
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return <LoadingState rows={3} label={label} />;
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return <SharedEmptyState title={title}>{children}</SharedEmptyState>;
}

// ---- Dialogs ------------------------------------------------------------------------------------

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent wide={wide} {...(description ? {} : { 'aria-describedby': undefined })}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {children ? <div className="flex flex-col gap-3">{children}</div> : null}
        {footer ? <DialogFooter>{footer}</DialogFooter> : null}
      </DialogContent>
    </Dialog>
  );
}

/** "Are you sure?" with one confirming action. */
export function ConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  danger,
  busy,
  error,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  error?: string | undefined;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {error ? <Notice tone="danger">{error}</Notice> : null}
    </Modal>
  );
}

// ---- Copy ---------------------------------------------------------------------------------------

const COPIED_MS = 2000;

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      // Clipboard blocked (permissions, insecure context): the text stays selectable on the page.
      setCopied(false);
    }
  }
  return (
    <Button size="sm" onClick={copy} aria-label={`${label}${copied ? ' (copied)' : ''}`}>
      {copied ? <Check aria-hidden="true" className="size-4" /> : <Copy aria-hidden="true" className="size-4" />}
      {copied ? 'Copied' : label}
    </Button>
  );
}

/** A preformatted block with a Copy button. */
export function CodeBlock({ code, label }: { code: string; label: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{label}</span>
        <CopyButton text={code} label={`Copy ${label.toLowerCase()}`} />
      </div>
      <pre className="overflow-x-auto rounded-md border border-border bg-surface-raised p-3 font-mono text-xs whitespace-pre-wrap break-all">
        {code}
      </pre>
    </div>
  );
}
